import React from "react";
import { cn } from "../../utils";
import { Popover, PopoverTrigger, PopoverContent } from "../popover/Popover";

interface ProvenanceBadgeProps {
  variant?: "table" | "chart";
  /** Rendered markdown content for the popover. */
  popoverContent?: React.ReactNode;
  /** Badge label. */
  badgeLabel?: string;
}

const BADGE_COLORS = {
  text: "al-text-teal-700 dark:al-text-teal-400",
  bg: "al-bg-teal-100 dark:al-bg-teal-950",
  border: "al-border-teal-300 dark:al-border-teal-700",
};

const BADGE_ICON = "\u25C6";

export const ProvenanceBadge: React.FC<ProvenanceBadgeProps> = ({
  variant = "table",
  popoverContent,
  badgeLabel,
}) => {
  if (!popoverContent) return null;

  const label = badgeLabel ?? "Data Source";

  if (variant === "table") {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "al-w-full al-flex al-items-center al-gap-1.5",
              "al-px-3 al-py-1.5 al-text-xs al-font-medium",
              "al-border al-border-b-0 al-rounded-t-md al-cursor-pointer",
              "al-transition-colors hover:al-opacity-90",
              BADGE_COLORS.bg,
              BADGE_COLORS.border,
              BADGE_COLORS.text
            )}
          >
            <span className="al-text-[10px]">{BADGE_ICON}</span>
            <span>Source: {label}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          side="bottom"
          className="al-w-80 al-p-3 al-overflow-auto al-max-h-72"
        >
          {popoverContent}
        </PopoverContent>
      </Popover>
    );
  }

  // Chart: floating chip (absolute positioned)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "al-absolute al-top-2 al-right-2 al-z-10",
            "al-inline-flex al-items-center al-gap-1",
            "al-px-2 al-py-0.5 al-rounded-md al-text-xs al-font-medium",
            "al-border al-shadow-sm al-cursor-pointer",
            "al-transition-opacity hover:al-opacity-80",
            BADGE_COLORS.bg,
            BADGE_COLORS.border,
            BADGE_COLORS.text
          )}
        >
          <span className="al-text-[10px]">{BADGE_ICON}</span>
          <span className="al-truncate al-max-w-[140px]">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="al-w-80 al-p-3 al-overflow-auto al-max-h-72"
      >
        {popoverContent}
      </PopoverContent>
    </Popover>
  );
};
